import React, { useState, useCallback, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';
import { PushNotificationStatus } from './PushNotificationStatus';
import toast from 'react-hot-toast';
import { 
  Smartphone, 
  Download, 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  CheckCircle,
  Bell,
  Settings,
  Trash2,
  Info,
  Shield,
  Zap,
  MessageSquare,
  TestTube
} from 'lucide-react';

export const PWASettings: React.FC = React.memo(() => {
  const {
    isInstallable,
    isInstalled,
    isOffline,
    hasUpdate,
    isStandalone,
    isPushSupported,
    pushSubscription,
    installPWA,
    applyUpdate,
    requestNotificationPermission,
    sendNotification,
    registerPushSubscription,
    unregisterPushSubscription,
    checkForUpdates
  } = usePWA();

  const [isInstalling, setIsInstalling] = useState(false);
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  const [isRegisteringPush, setIsRegisteringPush] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [testNotification, setTestNotification] = useState({ title: '', message: '' });

  // Verifica permissão de notificação atual
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (!isInstallable) return;
    
    setIsInstalling(true);
    try {
      await installPWA();
      toast.success('Aplicativo instalado com sucesso!');
    } catch (error) {
      console.error('Erro ao instalar:', error);
      toast.error('Erro ao instalar aplicativo');
    } finally {
      setIsInstalling(false);
    }
  }, [isInstallable, installPWA]);

  const handleUpdate = useCallback(async () => {
    try {
      await applyUpdate();
      toast.success('Aplicativo atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao aplicar atualização:', error);
      toast.error('Erro ao aplicar atualização');
    }
  }, [applyUpdate]);

  const handleCheckUpdates = useCallback(async () => {
    setIsCheckingUpdates(true);
    try {
      await checkForUpdates();
      toast.success('Verificação de atualizações concluída');
    } catch (error) {
      console.error('Erro ao verificar atualizações:', error);
      toast.error('Erro ao verificar atualizações');
    } finally {
      setIsCheckingUpdates(false);
    }
  }, [checkForUpdates]);

  const handleNotificationPermission = async () => {
    try {
      const result = await requestNotificationPermission();
      setNotificationPermission(result.permission as NotificationPermission);
      
      if (result.permission === 'granted') {
        // Testa a notificação
        await sendNotification('Notificações Ativadas!', {
          body: 'O HelpDesk agora pode enviar notificações importantes.',
          tag: 'notification-test'
        });
        toast.success('Notificações ativadas com sucesso!');
      } else {
        toast.error('Permissão de notificação negada');
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      toast.error('Erro ao solicitar permissão de notificação');
    }
  };

  const handleRegisterPush = async () => {
    if (!isPushSupported) return;
    
    setIsRegisteringPush(true);
    try {
      await registerPushSubscription();
      toast.success('Assinatura push registrada com sucesso!');
    } catch (error) {
      console.error('Erro ao registrar push:', error);
      toast.error('Erro ao registrar assinatura push: ' + (error as Error).message);
    } finally {
      setIsRegisteringPush(false);
    }
  };

  const handleUnregisterPush = async () => {
    if (!isPushSupported || !pushSubscription) return;
    
    try {
      await unregisterPushSubscription();
      toast.success('Assinatura push removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover push:', error);
      toast.error('Erro ao remover assinatura push: ' + (error as Error).message);
    }
  };

  const handleSendTestNotification = async () => {
    if (!testNotification.title || !testNotification.message) {
      toast.error('Preencha o título e a mensagem da notificação de teste');
      return;
    }

    try {
      await sendNotification(testNotification.title, {
        body: testNotification.message,
        tag: 'test-notification'
      });
      toast.success('Notificação de teste enviada!');
    } catch (error) {
      console.error('Erro ao enviar notificação de teste:', error);
      toast.error('Erro ao enviar notificação de teste: ' + (error as Error).message);
    }
  };

  const handleClearCache = async () => {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        toast.success('Cache limpo com sucesso!');
      } catch (error) {
        console.error('Erro ao limpar cache:', error);
        toast.error('Erro ao limpar cache');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="h-5 w-5" />;
      case 'warning': return <RefreshCw className="h-5 w-5" />;
      case 'error': return <WifiOff className="h-5 w-5" />;
      case 'info': return <Info className="h-5 w-5" />;
      default: return <Info className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Smartphone className="h-8 w-8 mr-3 text-blue-600" />
          Configurações PWA
        </h2>
        <p className="text-gray-600 mt-2">
          Gerencie as configurações do aplicativo progressivo web (PWA) e notificações push
        </p>
      </div>

      {/* Status do Sistema */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-blue-600" />
          Status do Sistema
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Status de Conexão */}
          <div className={`p-4 rounded-lg border ${getStatusColor(isOffline ? 'error' : 'success')}`}>
            <div className="flex items-center space-x-3">
              {getStatusIcon(isOffline ? 'error' : 'success')}
              <div>
                <p className="font-medium">Conexão</p>
                <p className="text-sm opacity-75">
                  {isOffline ? 'Offline' : 'Online'}
                </p>
              </div>
            </div>
          </div>

          {/* Status PWA */}
          <div className={`p-4 rounded-lg border ${getStatusColor(
            isStandalone ? 'success' : isInstallable ? 'warning' : 'info'
          )}`}>
            <div className="flex items-center space-x-3">
              {getStatusIcon(
                isStandalone ? 'success' : isInstallable ? 'warning' : 'info'
              )}
              <div>
                <p className="font-medium">Aplicativo</p>
                <p className="text-sm opacity-75">
                  {isStandalone ? 'Instalado' : isInstallable ? 'Pode Instalar' : 'Disponível'}
                </p>
              </div>
            </div>
          </div>

          {/* Status de Atualização */}
          {hasUpdate && (
            <div className={`p-4 rounded-lg border ${getStatusColor('warning')}`}>
              <div className="flex items-center space-x-3">
                {getStatusIcon('warning')}
                <div>
                  <p className="font-medium">Atualização</p>
                  <p className="text-sm opacity-75">Disponível</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Detalhado de Notificações Push */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="h-5 w-5 mr-2 text-blue-600" />
          Status das Notificações Push
        </h3>
        
        <PushNotificationStatus showDetails={true} />
      </div>

      {/* Ações */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="h-5 w-5 mr-2 text-blue-600" />
          Ações
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Instalar PWA */}
          {isInstallable && (
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="flex items-center justify-center px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-5 w-5 mr-2" />
              {isInstalling ? 'Instalando...' : 'Instalar Aplicativo'}
            </button>
          )}

          {/* Atualizar PWA */}
          {hasUpdate && (
            <button
              onClick={handleUpdate}
              className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Atualizar Aplicativo
            </button>
          )}

          {/* Verificar Atualizações */}
          <button
            onClick={handleCheckUpdates}
            disabled={isCheckingUpdates}
            className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${isCheckingUpdates ? 'animate-spin' : ''}`} />
            {isCheckingUpdates ? 'Verificando...' : 'Verificar Atualizações'}
          </button>

          {/* Configurar Notificações */}
          <button
            onClick={handleNotificationPermission}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
          >
            <Bell className="h-5 w-5 mr-2" />
            Configurar Notificações
          </button>

          {/* Registrar Push */}
          {isPushSupported && !pushSubscription && (
            <button
              onClick={handleRegisterPush}
              disabled={isRegisteringPush}
              className="flex items-center justify-center px-4 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              {isRegisteringPush ? 'Registrando...' : 'Ativar Push Notifications'}
            </button>
          )}

          {/* Remover Push */}
          {isPushSupported && pushSubscription && (
            <button
              onClick={handleUnregisterPush}
              className="flex items-center justify-center px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              Desativar Push Notifications
            </button>
          )}

          {/* Limpar Cache */}
          <button
            onClick={handleClearCache}
            className="flex items-center justify-center px-4 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
          >
            <Trash2 className="h-5 w-5 mr-2" />
            Limpar Cache
          </button>

          {/* Reset PWA Prompt (para testes) */}
          <button
            onClick={() => {
              if (typeof window !== 'undefined' && (window as any).resetPWADismiss) {
                (window as any).resetPWADismiss();
                toast.success('PWA Prompt resetado! Agora ele aparecerá novamente.');
              } else {
                toast.error('Função de reset não disponível');
              }
            }}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            <RefreshCw className="h-5 w-5 mr-2" />
            Reset PWA Prompt
          </button>
        </div>
      </div>

      {/* Teste de Notificações */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <TestTube className="h-5 w-5 mr-2 text-blue-600" />
          Teste de Notificações
        </h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título da Notificação
              </label>
              <input
                type="text"
                value={testNotification.title}
                onChange={(e) => setTestNotification(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Teste de Notificação"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem
              </label>
              <input
                type="text"
                value={testNotification.message}
                onChange={(e) => setTestNotification(prev => ({ ...prev, message: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Esta é uma notificação de teste"
              />
            </div>
          </div>
          
          <button
            onClick={handleSendTestNotification}
            disabled={!testNotification.title || !testNotification.message || notificationPermission !== 'granted'}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Bell className="h-5 w-5 mr-2" />
            Enviar Notificação de Teste
          </button>
        </div>
      </div>

      {/* Informações */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Info className="h-5 w-5 mr-2 text-blue-600" />
          Informações
        </h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Funcionalidades PWA</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Funcionamento offline</li>
                <li>• Instalação como aplicativo</li>
                <li>• Notificações push</li>
                <li>• Atualizações automáticas</li>
                <li>• Interface nativa</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-900 mb-2">Push Notifications</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Notificações em tempo real</li>
                <li>• Funciona mesmo com app fechado</li>
                <li>• Personalização avançada</li>
                <li>• Ações interativas</li>
                <li>• Criptografia segura</li>
              </ul>
            </div>
          </div>

          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h4 className="font-medium text-yellow-900 mb-2">Dicas de Uso</h4>
            <ul className="text-sm text-yellow-800 space-y-2">
              <li><strong>Instalação:</strong> Use o botão "Instalar Aplicativo" quando disponível</li>
              <li><strong>Offline:</strong> O app funciona mesmo sem internet para funcionalidades básicas</li>
              <li><strong>Notificações:</strong> Ative as notificações para receber alertas importantes</li>
              <li><strong>Push:</strong> Registre-se para receber notificações em tempo real</li>
              <li><strong>Atualizações:</strong> Mantenha o app atualizado para melhor performance</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Segurança */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2 text-blue-600" />
          Segurança e Privacidade
        </h3>
        
        <div className="space-y-3 text-sm text-gray-600">
          <p>• O PWA usa HTTPS para comunicação segura</p>
          <p>• Dados são armazenados localmente no seu dispositivo</p>
          <p>• Notificações só são enviadas com sua permissão</p>
          <p>• Push notifications usam criptografia VAPID</p>
          <p>• O cache é limpo automaticamente quando necessário</p>
          <p>• Todas as funcionalidades seguem as melhores práticas de segurança</p>
        </div>
      </div>
    </div>
  );
});

export default PWASettings;